import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Card } from "@/components/ui/card";
import { requestOtp, verifyOtp, apiErr } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic OTP login form
 * props: scope, tenantSlug, role, title, subtitle, onSuccess(data), accentColor
 */
export default function OtpLogin({
  scope,
  tenantSlug,
  role,
  title,
  subtitle,
  onSuccess,
  brandColor = "#0F172A",
  testIdPrefix = "login",
}) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [devOtp, setDevOtp] = useState("");

  const send = async (e) => {
    e?.preventDefault?.();
    if (phone.trim().length < 10) { toast.error("Enter a valid 10-digit phone"); return; }
    setSending(true);
    try {
      const data = await requestOtp({ phone: phone.trim(), scope, tenant_slug: tenantSlug, role, name });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success("OTP sent (mock)");
      setStep(2);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSending(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) { toast.error("Enter 6-digit OTP"); return; }
    setVerifying(true);
    try {
      const data = await verifyOtp({ phone: phone.trim(), otp, scope, tenant_slug: tenantSlug, role, name });
      onSuccess?.(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setVerifying(false); }
  };

  return (
    <Card className="w-full max-w-md p-6 sm:p-8 bg-white border border-slate-200 shadow-sm">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold font-display tracking-tight" style={{ color: brandColor }}>{title}</h2>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>

      {step === 1 && (
        <form onSubmit={send} className="space-y-4">
          {scope === "storefront" && (
            <div>
              <Label htmlFor="name">Your Name (optional)</Label>
              <Input data-testid={`${testIdPrefix}-name-input`} id="name" placeholder="e.g. Ramesh" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              data-testid={`${testIdPrefix}-phone-input`}
              id="phone"
              type="tel"
              placeholder="10-digit phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 12))}
              maxLength={12}
              autoFocus
            />
          </div>
          <Button data-testid={`${testIdPrefix}-send-otp-btn`} type="submit" disabled={sending} className="w-full" style={{ backgroundColor: brandColor }}>
            {sending ? "Sending..." : "Send OTP"}
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verify} className="space-y-4">
          <div>
            <p className="text-sm text-slate-500">OTP sent to <span className="font-medium text-slate-700">+91 {phone}</span></p>
            {devOtp && (
              <div className="mt-2 text-xs px-3 py-2 bg-amber-50 border border-amber-200 rounded text-amber-900">
                <b>DEV MOCK OTP:</b> <code>{devOtp}</code> (or use universal <code>123456</code>)
              </div>
            )}
          </div>
          <div className="flex justify-center" data-testid={`${testIdPrefix}-otp-input`}>
            <InputOTP maxLength={6} value={otp} onChange={setOtp}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button data-testid={`${testIdPrefix}-verify-otp-btn`} type="submit" disabled={verifying} className="w-full" style={{ backgroundColor: brandColor }}>
            {verifying ? "Verifying..." : "Verify & Continue"}
          </Button>
          <button type="button" data-testid={`${testIdPrefix}-change-phone-btn`} onClick={() => { setStep(1); setOtp(""); }} className="text-xs text-slate-500 hover:text-slate-800 w-full text-center">
            Change phone number
          </button>
        </form>
      )}
    </Card>
  );
}
